/*
      ___           ___
     /\  \         /\  \
    /::\  \       /::\  \
   /:/\:\  \     /:/\ \  \
  /::\ \:\  \   _\:\ \ \  \
 /:/\:\ \:\__\ /\ \:\ \ \__\
 \/_|::\/:/  / \:\ \:\ \/__/
    |:|::/  /   \:\ \:\__\
    |:|\/__/     \:\/:/  /
    |:|  |        \::/  /
     \|__|         \/__/

    Developed by Ralph Shippam - 2019.
*/

import {ExistingProvider, Injectable, Optional} from '@angular/core';
import {Observable, ReplaySubject, Subscribable, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';
import {HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {MatDialogRef} from '@angular/material/dialog';
import {RSloadingModalComponent} from './rsloading.modal.component';

import {RSDialog} from '../rsnotify/rsdialog';
import {RSLoadingModalDefaults, RSLoadingModalSpinner} from './rsloading.modal.defaults';
import {RSDialogService} from '../rsnotify/rsdialog.service';
import {RSDialogComponent} from '../rsnotify/rsdialog.component';
import {WindowRef} from './windowref';

@Injectable({
  providedIn: 'root'
})
export class RSLoadingModalService {
    private dialogRef: MatDialogRef<any>;
    private onbeforeunload = null; // Previous value before we change it. This allows us to restore other peoples custom ones

    isLoading = false;

    constructor(
        @Optional() private dialogDefaults: RSLoadingModalDefaults,
        private notifier: RSDialogService,
        private window: WindowRef
    ) {}

    // NOTE TO SELF: USING THIS MEANS PEOPLE CAN STILL NAVIGATE WITH BROWSER BACK/FORWARD.
    // IMPLEMENT THE SHARED NAVIGATION GUARD (RSLoadingDeactivateGuardService) TO PROTECT AGAINST THIS
    public show(options: RSLoadingModalDefaults = {} as RSLoadingModalDefaults, useDefaults = true) {

        // Build the data object, {} + defaults? + userOpts
        const data: any = {};
        if (useDefaults) {
            Object.assign(data, this.dialogDefaults);
        }
        Object.assign(data, options);
        if (data.hasOwnProperty('dialogConfig')) {
            delete data.dialogConfig;
        }

        // Build MatDialogConfig, {} + defaults? + userOpts
        const dialogConfig: RSDialog.Options = {} as RSDialog.Options;
        if (useDefaults) {
            Object.assign(dialogConfig, this.dialogDefaults.dialogConfig);
        }
        Object.assign(dialogConfig, options.dialogConfig);

        // Assign the data
        data.close = this.closeDialog;
        dialogConfig.id = 'rsLoadingModel';
        dialogConfig.values = data;

        if (this.dialogDefaults.type !== RSLoadingModalSpinner.Component || !dialogConfig.component) {
            dialogConfig.component = RSloadingModalComponent;
        } // Else set by user

        if (options.beforeUnloadProtection) {
            this.onbeforeunload = this.window.nativeWindow.onbeforeunload;
            this.window.nativeWindow.onbeforeunload = this.deactivateWarning;
        }

        // Launch the dialog
        this.notifier.showDialog(dialogConfig).then(() => {
            this.dialogRef = null;
            this.isLoading = false;
        }, (e) => {
            this.dialogRef = null;
            this.isLoading = false;
        });

        // Get the handle for this dialog
        this.dialogRef = this.notifier.getDialogHandle(dialogConfig.id);
        this.isLoading = true;
    }

    deactivateWarning = (event) => {
        event.preventDefault();
        event.returnValue = 'Changes that you made may not be saved.';
    };

    public showProgress(updates: Subscribable<RSLoadingProgressUpdate>) {
        this.show({ type: RSLoadingModalSpinner.Progress } as RSLoadingModalDefaults);
        updates.subscribe(
            (update: RSLoadingProgressUpdate) => {
                const loadingModal = this.dialogRef.componentInstance.data.component.instance;
                loadingModal.dialogValues.progress = update.progress;
                loadingModal.dialogValues.progressTitle = update.title;
                loadingModal.dialogValues.mode = update.mode;
                loadingModal.dialogValuesUpdated.emit();
            }, (error) => {
                console.error(error);
                this.hide();
            }, () => {
                this.hide();
            }
        );
    }

    private closeDialog(value = RSDialog.defaultValues.CANCEL) {
        // Is it still our warning? If so, restore it back to whatever we started with
        if (this.window.nativeWindow.onbeforeunload === this.deactivateWarning) {
            this.window.nativeWindow.onbeforeunload = this.onbeforeunload || null;
        }

        if (this.dialogRef) {
            this.dialogRef.close(value);
        }
        this.isLoading = false;
    }

    public hide(): void {
        this.closeDialog();
    }

}

export interface RSLoadingProgressUpdate {
    title: string;
    progress: number; // percent
    mode: 'indeterminate' | 'determinate'; // style
}

@Injectable()
export class RSLoadingDeactivateGuardService  {
    constructor(private loading: RSLoadingModalService) {}
    canDeactivate(component) {
        if (!component) {
            console.warn('RSLoadingDeactivateGuardService::: component === null');
            return true;
        } else {
            const canChange = !this.loading.isLoading;
            if (!canChange) {
                console.log('RSLoadingDeactivateGuardService::: Blocking navigation!', component);
            }
            return canChange;
        }
    }
}

// HTTP Interceptor and HTTP Loading
@Injectable({
    providedIn: 'root'
})
export class RSLoadingHTTP implements HttpInterceptor {
    private _pendingRequests = 0;
    private _pendingRequestsStatus: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    get pendingRequestsStatus$(): Observable<boolean> {
        return this._pendingRequestsStatus.asObservable();
    }

    get pendingRequests(): number {
        return this._pendingRequests;
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const shouldBypass = false;
        if (!shouldBypass) {
            this._pendingRequests++;

            if (1 === this._pendingRequests) {
                this._pendingRequestsStatus.next(true);
            }
        }

        return next.handle(req).pipe(
            map(event => {
                return event;
            }),
            catchError(error => {
                return throwError(error);
            }),
            finalize(() => {
                if (!shouldBypass) {
                    this._pendingRequests--;

                    if (0 === this._pendingRequests) {
                        this._pendingRequestsStatus.next(false);
                    }
                }
            })
        );
    }

}
export const RSLoadingHTTPInterceptor: ExistingProvider[] = [{
    provide: HTTP_INTERCEPTORS,
    useExisting: RSLoadingHTTP,
    multi: true
}];
